/**
 * LOS FRENOS QUE EL CLUB LE PONE A LOS RETOS POR RANKING.
 *
 * Javier: «quiero en el club poder configurar estos parámetros como
 * administrador».
 *
 * QUÉ PROBLEMA RESUELVEN. El reto no tenía más freno que «los dos en el mismo
 * club y el mismo ranking». Sin nada más, quien tiene la tarde libre reta a
 * diez personas el mismo día y el ranking pasa a medir tiempo disponible; dos
 * amigos se turnan las victorias y suben los dos; y el último de la tabla reta
 * al primero, pierde 6-0 6-0 y se lleva puntos por haber jugado.
 *
 * CADA COSA SE MANDA SOLA, igual que los interruptores de partido: si el
 * guardado llevara los cinco campos, dos personas administrando el mismo club
 * se pisarían —quien toca uno reescribiría los otros cuatro con lo que tenía
 * en su pantalla, que puede ser viejo—.
 *
 * LO QUE ESTÁ APAGADO NO MUESTRA SUS DETALLES. Con todo desplegado son nueve
 * campos de golpe y ninguno se entiende; cada bloque aparece cuando su
 * interruptor está encendido, que es cuando su número significa algo.
 *
 * LOS NÚMEROS SE GUARDAN AL SALIR DEL CAMPO y no en cada tecla: guardando al
 * teclear, escribir «12» manda primero un 1 —que es una configuración válida
 * y distinta— y el club queda un instante con una regla que nadie eligió.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHALLENGE_SETTINGS_OFF,
  CHALLENGE_SETTING_LIMITS,
  type Community,
  type SetChallengeSettingsRequest,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

/** Lunes a domingo, en ISO. El orden de la semana tica arranca en lunes. */
const DÍAS = [1, 2, 3, 4, 5, 6, 7] as const;

export function ChallengeSettings({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: (community: Community) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * EL BLOQUE, DEFENDIDO. `ChallengeSettings` es una promesa sobre el
   * contrato, no una garantía sobre el cable: un servidor una versión atrás
   * manda el club sin esto, y leer `.enabled` de `undefined` tira la pantalla
   * entera de configuración —la de quien administra, o sea la única desde la
   * que se podría arreglar algo—.
   */
  const config = community.challengeSettings ?? CHALLENGE_SETTINGS_OFF;
  const días = Array.isArray(config.resetDays) ? config.resetDays : [];

  async function guardar(values: SetChallengeSettingsRequest): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.setChallengeSettings(community.id, values));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auto-approve">
      <label className="check">
        <input
          type="checkbox"
          checked={config.enabled}
          disabled={busy}
          onChange={(e) => void guardar({ enabled: e.target.checked })}
        />
        <span>{t('challengeSettings.enabled')}</span>
      </label>
      <p className="help">
        {config.enabled ? t('challengeSettings.enabledOn') : t('challengeSettings.enabledOff')}
      </p>

      {config.enabled && (
        <>
          {/* --- La tanda ------------------------------------------------ */}
          <h4>{t('challengeSettings.resetTitle')}</h4>
          <p className="help">{t('challengeSettings.resetHelp')}</p>

          <div className="day-picker">
            {DÍAS.map((día) => {
              const puesto = días.includes(día);
              return (
                <button
                  key={día}
                  type="button"
                  className={puesto ? 'day-chip on' : 'day-chip'}
                  aria-pressed={puesto}
                  disabled={busy}
                  onClick={() =>
                    void guardar({
                      resetDays: puesto ? días.filter((d) => d !== día) : [...días, día],
                    })
                  }
                >
                  {t(`challengeSettings.day${día}`)}
                </button>
              );
            })}
          </div>

          {/* SIN DÍAS NO HAY TANDA, así que el número no significa nada y no
              se muestra: un campo que no hace nada es peor que no tenerlo. */}
          {días.length > 0 && (
            <NumberRow
              id="challenge-per-reset"
              label={t('challengeSettings.perReset')}
              help={t('challengeSettings.perResetHelp')}
              value={config.perReset}
              min={CHALLENGE_SETTING_LIMITS.perResetMin}
              max={CHALLENGE_SETTING_LIMITS.perResetMax}
              disabled={busy}
              onCommit={(perReset) => void guardar({ perReset })}
            />
          )}

          {/* --- Repetir rival ------------------------------------------- */}
          <h4>{t('challengeSettings.repeatTitle')}</h4>
          <NumberRow
            id="challenge-repeat-after"
            label={t('challengeSettings.repeatAfter')}
            help={t('challengeSettings.repeatAfterHelp')}
            value={config.repeatAfter}
            min={CHALLENGE_SETTING_LIMITS.repeatAfterMin}
            max={CHALLENGE_SETTING_LIMITS.repeatAfterMax}
            disabled={busy}
            onCommit={(repeatAfter) => void guardar({ repeatAfter })}
          />

          {/* --- Los plazos ---------------------------------------------- */}
          <h4>{t('challengeSettings.deadlinesTitle')}</h4>
          <p className="help">{t('challengeSettings.deadlinesHelp')}</p>

          <DeadlineRow
            id="challenge-accept-days"
            label={t('challengeSettings.acceptDays')}
            help={t('challengeSettings.acceptDaysHelp')}
            toggle={t('challengeSettings.acceptDaysOn')}
            value={config.acceptDays}
            porOmisión={2}
            disabled={busy}
            onChange={(acceptDays) => void guardar({ acceptDays })}
          />

          <DeadlineRow
            id="challenge-play-days"
            label={t('challengeSettings.playDays')}
            help={t('challengeSettings.playDaysHelp')}
            toggle={t('challengeSettings.playDaysOn')}
            value={config.playDays}
            porOmisión={14}
            disabled={busy}
            onChange={(playDays) => void guardar({ playDays })}
          />

          {/* --- Las penalidades ------------------------------------------ */}

          {/* SOLO SI HAY PLAZO. Una penalidad por «no contestar a tiempo» sin
              un tiempo definido no se puede cobrar nunca, y ofrecerla igual
              sería un interruptor que no hace nada. */}
          {(config.acceptDays !== null || config.playDays !== null) && (
            <>
              <h4>{t('challengeSettings.penaltiesTitle')}</h4>
              <p className="help">{t('challengeSettings.penaltiesHelp')}</p>

              {config.acceptDays !== null && (
                <DeadlineRow
                  id="challenge-penalty-unanswered"
                  label={t('challengeSettings.penaltyUnanswered')}
                  help={t('challengeSettings.penaltyUnansweredHelp')}
                  toggle={t('challengeSettings.penaltyUnansweredOn')}
                  value={config.penaltyUnanswered}
                  porOmisión={5}
                  min={CHALLENGE_SETTING_LIMITS.penaltyMin}
                  max={CHALLENGE_SETTING_LIMITS.penaltyMax}
                  disabled={busy}
                  onChange={(penaltyUnanswered) => void guardar({ penaltyUnanswered })}
                />
              )}

              {config.playDays !== null && (
                <DeadlineRow
                  id="challenge-reward-unplayed"
                  label={t('challengeSettings.rewardUnplayed')}
                  help={t('challengeSettings.rewardUnplayedHelp')}
                  toggle={t('challengeSettings.rewardUnplayedOn')}
                  value={config.rewardUnplayed}
                  porOmisión={5}
                  min={CHALLENGE_SETTING_LIMITS.penaltyMin}
                  max={CHALLENGE_SETTING_LIMITS.penaltyMax}
                  disabled={busy}
                  onChange={(rewardUnplayed) => void guardar({ rewardUnplayed })}
                />
              )}
            </>
          )}

          {/* --- La inactividad ------------------------------------------- */}

          <h4>{t('challengeSettings.inactivityTitle')}</h4>
          <DeadlineRow
            id="challenge-inactivity"
            label={t('challengeSettings.inactivityPenalty')}
            help={t('challengeSettings.inactivityHelp')}
            toggle={t('challengeSettings.inactivityOn')}
            value={config.inactivityPenalty}
            // Uno, y no cinco como las otras: esta se cobra TODAS LAS SEMANAS
            // y a todo el mundo a la vez. Arrancar en cinco sería sacarle
            // veinte puntos al mes a medio club con un solo clic.
            porOmisión={1}
            min={CHALLENGE_SETTING_LIMITS.penaltyMin}
            max={CHALLENGE_SETTING_LIMITS.penaltyMax}
            disabled={busy}
            onChange={(inactivityPenalty) => void guardar({ inactivityPenalty })}
          />

          {/* --- El límite de posiciones --------------------------------- */}
          <h4>{t('challengeSettings.limitTitle')}</h4>
          <label className="check">
            <input
              type="checkbox"
              checked={config.positionLimit !== null}
              disabled={busy}
              onChange={(e) =>
                // Prenderlo arranca en cinco y no en cero: cero es «no se puede
                // retar hacia arriba», que es una regla dura para estrenar sola
                // con un clic. Apagarlo manda `null` explícito, que es lo que
                // borra el límite.
                void guardar({ positionLimit: e.target.checked ? 5 : null })
              }
            />
            <span>{t('challengeSettings.limitPositions')}</span>
          </label>
          <p className="help">{t('challengeSettings.limitHelp')}</p>

          {config.positionLimit !== null && (
            <NumberRow
              id="challenge-position-limit"
              label={t('challengeSettings.positionLimit')}
              help={t('challengeSettings.positionLimitHelp')}
              value={config.positionLimit}
              min={CHALLENGE_SETTING_LIMITS.positionLimitMin}
              max={CHALLENGE_SETTING_LIMITS.positionLimitMax}
              disabled={busy}
              onCommit={(positionLimit) => void guardar({ positionLimit })}
            />
          )}
        </>
      )}

      {error !== null && <Alert kind="error">{error}</Alert>}
    </div>
  );
}

/**
 * UN NÚMERO QUE SE GUARDA AL SALIR DEL CAMPO.
 *
 * El borrador es local mientras se escribe y se manda al perder el foco. Al
 * teclear, escribir «12» mandaría primero un 1 —una configuración válida y
 * distinta— y el club quedaría un instante con una regla que nadie eligió.
 *
 * Y SE ACOTA ANTES DE MANDAR: el servidor rechaza lo que está fuera de rango,
 * pero un error rojo por haber escrito un 0 de más es peor que guardar el
 * tope. El campo vacío no manda nada, que es lo que pasa mientras alguien
 * borra para volver a escribir.
 */
function NumberRow({
  id,
  label,
  help,
  value,
  min,
  max,
  disabled,
  onCommit,
}: {
  id: string;
  label: string;
  help: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(String(value));

  // Cuando el guardado vuelve con otro valor —o cuando otra persona lo
  // cambió— el campo tiene que mostrarlo. Sin esto, el borrador local se
  // queda con lo que se escribió aunque el servidor haya guardado otra cosa.
  useEffect(() => setDraft(String(value)), [value]);

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const leído = Number.parseInt(draft, 10);
          if (Number.isNaN(leído)) {
            setDraft(String(value));
            return;
          }
          const acotado = Math.min(max, Math.max(min, leído));
          setDraft(String(acotado));
          if (acotado !== value) onCommit(acotado);
        }}
      />
      <p className="help">{help}</p>
    </div>
  );
}

/**
 * UNA CASILLA PARA TENERLO O NO, Y UN NÚMERO CUANDO LO HAY.
 *
 * Sirve para los plazos y para las penalidades, que son la misma forma:
 * «¿existe esto?» y «¿de cuánto?». Por eso recibe el rango en vez de
 * suponerlo — los días van hasta 90 y los puntos hasta 100.
 *
 * SON DOS PREGUNTAS Y NO UNA. «¿Hay plazo?» y «¿de cuántos días?» se contestan
 * en momentos distintos, y un solo campo numérico donde el cero significara
 * «sin plazo» sería una regla escondida adentro de un número: nadie adivina
 * que escribir 0 apaga algo, y quien lo escriba sin querer va a creer que puso
 * un plazo durísimo.
 *
 * PRENDERLO ARRANCA EN UN VALOR RAZONABLE —dos días para contestar, catorce
 * para jugar, que son los de la aplicación de la que salió la idea— y no en
 * uno: un plazo de un día para jugar un partido de tenis es imposible de
 * cumplir, y estrenarlo así le vencería retos a medio club el primer día.
 */
function DeadlineRow({
  id,
  label,
  help,
  toggle,
  value,
  porOmisión,
  min = CHALLENGE_SETTING_LIMITS.daysMin,
  max = CHALLENGE_SETTING_LIMITS.daysMax,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  /** El texto de la casilla que decide si esto existe. */
  toggle: string;
  value: number | null;
  porOmisión: number;
  min?: number;
  max?: number;
  disabled: boolean;
  onChange: (value: number | null) => void;
}): JSX.Element {
  return (
    <>
      <label className="check">
        <input
          type="checkbox"
          checked={value !== null}
          disabled={disabled}
          // Apagarlo manda `null` EXPLÍCITO, que es lo que quita el plazo. No
          // mandarlo lo dejaría como estaba, que es lo contrario del clic.
          onChange={(e) => onChange(e.target.checked ? porOmisión : null)}
        />
        <span>{toggle}</span>
      </label>

      {value !== null && (
        <NumberRow
          id={id}
          label={label}
          help={help}
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          onCommit={onChange}
        />
      )}
    </>
  );
}
